import type { FlagsReturnType } from '@/composables/useFeatureFlags'
import { assistantState, checkInjectAssistantJs } from './assistantMode'
import { unmountCommentatorUIWebComponent } from './commentator'

let drillSliderObserverCleanup: (() => void) | null = null

export const toggleAiChatButtonVisibilityOnDrillSlideChange = (chatUI: HTMLElement) => {
  const targetNode = document.querySelector('#nlhe-mobile-swiper')
  const aiChatButton = chatUI.shadowRoot?.querySelector<HTMLButtonElement>('#ai-coach-button')
  if (!targetNode || !aiChatButton) return

  const updateVisibility = (slideName: string | null) => {
    aiChatButton.style.display = slideName === 'Table' ? 'block' : 'none'
  }

  updateVisibility(targetNode.getAttribute('data-slidename'))

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'data-slidename') {
        const newSlideName = (mutation.target as HTMLElement).getAttribute('data-slidename')
        updateVisibility(newSlideName)
      }
    }
  })

  observer.observe(targetNode, {
    attributes: true,
    attributeFilter: ['data-slidename']
  })

  drillSliderObserverCleanup = () => observer.disconnect()

  return drillSliderObserverCleanup
}

export async function mountChatUIWebComponent({
  userId,
  playerId,
  tableId,
  mode,
  nickname,
  flags
}: {
  userId?: string
  playerId?: string
  tableId?: string
  mode: 'home' | 'compete' | 'drill' | 'player-chat-only'
  nickname?: string
  flags: FlagsReturnType
}) {
  const query = new URLSearchParams(window.location.search)

  if (!window.assistantMode) window.assistantMode = {}
  if (!window.assistantMode.params) window.assistantMode.params = {}

  window.assistantMode.params.gameCode =
    assistantState.gameCode || (query.get('gameCode') as string)
  window.assistantMode.params.roomId = tableId
  window.assistantMode.params.userId = userId
  window.assistantMode.params.playerId = playerId
  window.assistantMode.params.nickname = nickname || query.get('nickname') || 'Player'

  checkInjectAssistantJs()
  await assistantState.scriptLoadPromise

  let chatUI = document.getElementById('chat-ui')

  if (!chatUI) {
    chatUI = document.createElement('chat-ui')
    chatUI.id = 'chat-ui'
    document.body.appendChild(chatUI)
  }

  const isUserChatOnly = !!flags.isEnabled('quintace_user_user_chat_only').value
  const isAiHostChatNoAction = !!flags.isEnabled('quintace_home_ai_host_chat_no_action').value
  // `SETTINGS_MAP` overrides the `CHAT_TYPE_MAP`
  const SETTINGS_MAP = {
    HIDE_POPOVER: false,
    HIDE_AI_CHAT_BUTTON: mode === 'home' || isUserChatOnly,
    HIDE_USER_CHAT_BUTTON: false,
    HIDE_ALL: false
  }

  const CHAT_TYPE_MAP: Record<
    string,
    {
      chatType: 'ai-chat' | 'user-chat' | 'both'
      customElementId: string | null
      disabled: boolean
    }
  > = {
    // `chatType` value can be `ai-chat | user-chat | both`
    home: {
      chatType: 'ai-chat',
      customElementId: '#bottom-nav-coach__loader',
      disabled: !!isAiHostChatNoAction
    },
    compete: {
      chatType: isUserChatOnly ? 'user-chat' : 'both',
      customElementId: null,
      disabled: false
    },
    drill: {
      chatType: 'ai-chat',
      customElementId: null,
      disabled: false
    }
  }

  chatUI.setAttribute('mode', JSON.stringify(CHAT_TYPE_MAP[mode]))
  chatUI.setAttribute('settings', JSON.stringify(SETTINGS_MAP))

  if (mode === 'drill') toggleAiChatButtonVisibilityOnDrillSlideChange(chatUI)
}

export function unmountChatUIWebComponent() {
  const chatUI = document.getElementById('chat-ui')

  if (drillSliderObserverCleanup) drillSliderObserverCleanup()

  if (chatUI) {
    assistantState.numberOfPlayers = 0
    chatUI.remove()
  }

  unmountCommentatorUIWebComponent()
}
